import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  CreditCard,
  Code,
  Refresh,
  ContentCopy,
} from "@mui/icons-material";
import axios from "axios";

/**
 * Company Settings Component - Manages company profile and settings
 *
 * Allows company admins to update company information, manage settings,
 * and configure company-specific preferences
 *
 * @param {Object} props - Component props
 * @param {number} props.companyId - The company ID to manage settings for
 * @returns {JSX.Element} - The rendered company settings component
 */
function CompanySettings({ companyId }) {
  const [companyDetails, setCompanyDetails] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regeneratingCode, setRegeneratingCode] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // API endpoints
  const getCompanyDetailsApi =
    process.env.REACT_APP_API_URL + "/api/get-company-details";
  const editCompanyApi = process.env.REACT_APP_API_URL + "/api/edit-company";

  /**
   * Gets the JWT token from cookies for API authentication
   */
  const getAuthToken = () => {
    const cookies = document.cookie.split(";");
    let token = null;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_auth") {
        token = decodeURIComponent(value);
        break;
      }
    }

    return token;
  };

  /**
   * Fetch company details from API
   */
  const fetchCompanyDetails = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      const apiUrl = `${getCompanyDetailsApi}?companyId=${companyId}`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (response.data && response.data.success) {
        const company = response.data.data;
        setCompanyDetails(company);
        setFormData({
          name: company.name || "",
          description: company.description || "",
        });
      } else {
        setError("Failed to load company details");
      }
    } catch (error) {
      console.error("Error fetching company details:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Failed to load company details. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, getCompanyDetailsApi]);

  // Only fetch data when companyId is available
  useEffect(() => {
    if (companyId) {
      fetchCompanyDetails();
    } else {
      // If no companyId, set loading to false but don't show error yet
      setLoading(false);
    }
  }, [companyId, fetchCompanyDetails]);

  /**
   * Handle form input changes
   */
  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  /**
   * Start editing mode
   */
  const handleEdit = () => {
    setEditMode(true);
    setError(null);
    setSuccess(null);
  };

  /**
   * Cancel editing mode and reset form data
   */
  const handleCancel = () => {
    setEditMode(false);
    setError(null);
    setSuccess(null);
    // Reset form data to original values
    if (companyDetails) {
      setFormData({
        name: companyDetails.name || "",
        description: companyDetails.description || "",
      });
    }
  };

  /**
   * Save company details using the edit-company API
   */
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();

      // Prepare data for the edit-company API
      const updateData = {
        tid: companyDetails.tid,
        name: formData.name,
        description: formData.description,
        meeting_creds: companyDetails.meeting_cred, // Keep existing credits
        reg_code: "False", // Don't regenerate code during regular save
      };

      const response = await axios.post(editCompanyApi, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data && response.data.Message === "Company Modified!") {
        // Update local state with new values
        setCompanyDetails((prev) => ({
          ...prev,
          name: formData.name,
          description: formData.description,
        }));

        setEditMode(false);
        setSuccess("Company details updated successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to update company details. Please try again.");
      }
    } catch (error) {
      console.error("Error updating company details:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Failed to update company details. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Regenerate company registration code
   */
  const handleRegenerateCode = async () => {
    setRegeneratingCode(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();

      // Prepare data for regenerating registration code
      const updateData = {
        tid: companyDetails.tid,
        name: companyDetails.name,
        description: companyDetails.description,
        meeting_creds: companyDetails.meeting_cred,
        reg_code: "True", // This triggers code regeneration
      };

      const response = await axios.post(editCompanyApi, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data && response.data.Message === "Company Modified!") {
        // Refresh company details to get the new registration code
        await fetchCompanyDetails();
        setSuccess("Registration code regenerated successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to regenerate registration code. Please try again.");
      }
    } catch (error) {
      console.error("Error regenerating registration code:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Failed to regenerate registration code. Please try again.");
      }
    } finally {
      setRegeneratingCode(false);
    }
  };

  /**
   * Copy registration code to clipboard
   */
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(companyDetails.reg_code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy registration code:", error);
    }
  };

  /**
   * Generate company initials for avatar
   */
  const getCompanyInitials = (name) => {
    if (!name) return "C";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography
          variant='body1'
          color='text.secondary'
        >
          Loading company settings...
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
        >
          Company ID: {companyId || "Waiting for authentication..."}
        </Typography>
      </Box>
    );
  }

  // If no companyId is provided and we're not loading, show a waiting message
  if (!companyId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography
          variant='h6'
          color='text.secondary'
        >
          Waiting for authentication...
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          Please wait while we load your company information.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {error && (
        <Alert
          severity='error'
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity='success'
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {copySuccess && (
        <Alert
          severity='info'
          sx={{ mb: 2 }}
        >
          Registration code copied to clipboard!
        </Alert>
      )}

      <Grid
        container
        spacing={3}
      >
        {/* Company Profile */}
        <Grid
          item
          xs={12}
          md={8}
        >
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant='h6'
                fontWeight='bold'
              >
                Company Profile
              </Typography>
              {!editMode ? (
                <Button
                  variant='outlined'
                  startIcon={<Edit />}
                  onClick={handleEdit}
                >
                  Edit Profile
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant='outlined'
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    startIcon={
                      saving ? <CircularProgress size={16} /> : <Save />
                    }
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Box>
              )}
            </Box>

            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
                sm={4}
              >
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "primary.main",
                      fontSize: "2rem",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    {getCompanyInitials(companyDetails?.name)}
                  </Avatar>
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                  >
                    {companyDetails?.name || "Company Name"}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    Company ID: {companyDetails?.tid}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    Created: {formatDate(companyDetails?.create_date)}
                  </Typography>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sm={8}
              >
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      fullWidth
                      label='Company Name'
                      value={formData.name}
                      onChange={handleInputChange("name")}
                      disabled={!editMode}
                      variant={editMode ? "outlined" : "filled"}
                      required
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      fullWidth
                      label='Description'
                      value={formData.description}
                      onChange={handleInputChange("description")}
                      disabled={!editMode}
                      variant={editMode ? "outlined" : "filled"}
                      multiline
                      rows={3}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Company Stats & Registration Code */}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Grid
            container
            spacing={3}
          >
            {/* Company Overview */}
            <Grid
              item
              xs={12}
            >
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{ mb: 2 }}
                >
                  Company Overview
                </Typography>
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <CreditCard color='primary' />
                      <Box>
                        <Typography
                          variant='h5'
                          fontWeight='bold'
                        >
                          {companyDetails?.meeting_cred || 0}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                        >
                          Available Credits
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Registration Code Management */}
            <Grid
              item
              xs={12}
            >
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{ mb: 2 }}
                >
                  Registration Code
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Code color='secondary' />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant='h6'
                      fontWeight='bold'
                      sx={{ fontFamily: "monospace" }}
                    >
                      {companyDetails?.reg_code || "N/A"}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      Share this code with new employees
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Tooltip title='Copy to clipboard'>
                      <IconButton
                        onClick={handleCopyCode}
                        size='small'
                        color='primary'
                      >
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Generate new code'>
                      <IconButton
                        onClick={handleRegenerateCode}
                        disabled={regeneratingCode}
                        size='small'
                        color='secondary'
                      >
                        {regeneratingCode ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Refresh />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: "block", textAlign: "center" }}
                >
                  Warning: Regenerating the code will invalidate the current one
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CompanySettings;
